import type { MouseEvent, ReactElement } from "react";
import { SmallNumber, logical } from "../../ui/kit/index.ts";
import { fitsSmallDigits } from "../../ui/kit/small-digits.ts";
import { ABILITY_SLOT } from "../bar/layout.ts";
import { BAR_PAGE_COUNT, pageSlots } from "../bar/pages.ts";
import { swapLabel } from "../bar/words.ts";
import { useKeyHints } from "../touch/use-key-hints.ts";
import { slotArt } from "./slot-art.ts";
import { SLOT_ICONS } from "./slot-icons.ts";
import { useKeyLabel } from "../controls/use-key.ts";

/**
 * The swap control of a paged ability bar (P1.9-T133; ADR-061's note for P1.9-T133; the design's touch swap button in
 * `ui_kits/surfaces/TouchSurfaces.jsx`, drawn at a slot's size for a mouse): a slot's frame a gap after the fifth
 * slot shown, the drawn swap icon in its middle (the two arrows, `slot-icons.ts`), the page as "1/2" in the kit's small digits at the bottom right where a
 * slot prints its cost, and its key at the top left while the viewer's Show key numbers on the bar is on (P2.18-T15),
 * which its accessible name says either way. A click, Enter or Space shows the other page; a pointer press leaves
 * focus where it was, as a slot's does. Each key press for a slot not shown flashes its edge once in the focus colour.
 * While a drag rests on it, counting toward the other page, it draws as a slot that takes the drop does (P2.8-T30,
 * ADR-121's note of 2026-10-12).
 */

/**
 * The drawn swap icon's box in the slot: the 16 pixel art centred inside the slot's ring (`slot-art.ts`).
 */
const art = slotArt();

/**
 * Keeps a pointer press from taking focus, so the control left focused never answers Space or Enter, which the game
 * captures.
 *
 * @param event The press.
 */
function keepFocus(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

/**
 * The swap control.
 *
 * @param props The `page` shown, from 0; `flash`, how many key presses for a slot not shown it has flashed for, each
 *   change starting one flash; whether it draws its key (`showKey`); whether a drag rests on it (`resting`); and
 *   `onSwap`, which a click calls.
 * @returns The control.
 */
export function BarSwap({ page, flash, showKey, resting, onSwap }: { readonly page: number; readonly flash: number; readonly showKey: boolean; readonly resting: boolean; readonly onSwap: () => void }): ReactElement {
  const key = useKeyLabel("swapPage");
  const keyed = useKeyHints();
  return (
    <button type="button" className={`ot-hud-ability ot-hud-swap${resting ? " is-drop-target" : ""}`} data-hud="ability-swap" data-page={page + 1} data-resting={resting ? "on" : undefined} aria-label={swapLabel(page, BAR_PAGE_COUNT, pageSlots(page), key, keyed)} onMouseDown={keepFocus} onClick={onSwap} style={{ width: logical(ABILITY_SLOT.size), height: logical(ABILITY_SLOT.size) }}>
      <img className="ot-hud-swap__art" src={SLOT_ICONS.swap} alt="" draggable={false} style={{ left: logical(art.left), top: logical(art.top), width: logical(art.size), height: logical(art.size) }} />
      {!showKey ? null : fitsSmallDigits(key) ? (
        <SmallNumber text={key} className="ot-hud-ability__key" place={{ left: 0, top: 0 }} />
      ) : (
        <span className="ot-hud-ability__key" aria-hidden="true">
          {key}
        </span>
      )}
      <SmallNumber text={`${page + 1}/${BAR_PAGE_COUNT}`} className="ot-hud-swap__page" hud="ability-swap-page" place={{ right: 0, bottom: 0 }} />
      {flash === 0 ? null : <span key={flash} className="ot-hud-swap__flash" data-hud="ability-swap-flash" aria-hidden="true" />}
    </button>
  );
}
